import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sparkles, User, Menu, X, Shield, MessageSquarePlus, Package, PenTool, Compass } from 'lucide-react';
import SettingsModal from './SettingsModal';
import FeedbackWidget from './FeedbackWidget';

const Navigation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Main 3-tab navigation
  const mainNavItems = [
    { path: '/stash', label: 'My Stash', icon: Package },
    { path: '/create', label: 'Create', icon: PenTool },
    { path: '/explore', label: 'Explore', icon: Compass },
  ];

  const isActive = (path) => {
    // Handle nested routes
    if (path === '/stash') return location.pathname === '/stash' || location.pathname.startsWith('/stash/');
    if (path === '/create') return location.pathname === '/create' || location.pathname.startsWith('/my-designs');
    if (path === '/explore') return location.pathname === '/explore' || location.pathname === '/community' || location.pathname.startsWith('/designs/');
    return location.pathname === path;
  };

  return (
    <>
      {/* Top Navigation Bar */}
      <nav className="bg-white border-b border-beige sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div 
              className="flex items-center space-x-2 shrink-0 cursor-pointer"
              onClick={() => navigate('/stash')}
            >
              <Sparkles className="h-6 w-6 text-sage" />
              <div className="flex flex-col">
                <span className="text-xl font-light text-charcoal leading-tight whitespace-nowrap">Kura Stash</span>
                <span className="text-xs text-gray-soft">from <a href="https://kura.shop" target="_blank" rel="noopener noreferrer" className="text-sage hover:text-sage-dark underline" onClick={(e) => e.stopPropagation()}>Kura Studio</a></span>
              </div>
            </div>

            {/* Desktop Navigation - 3 Main Tabs */}
            <div className="hidden lg:flex items-center min-w-0">
              <div className="flex items-center gap-x-1 mr-4">
                {mainNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive(item.path)
                          ? 'bg-sage text-white'
                          : 'text-charcoal hover:bg-cream'
                      }`}
                      data-testid={`nav-${item.path.slice(1)}`}
                    >
                      <Icon size={16} />
                      {item.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center space-x-2 pl-3 border-l border-beige shrink-0">
                {/* Feedback & Admin icons on the LEFT of avatar */}
                <button
                  onClick={() => setShowFeedback(true)}
                  className="p-1.5 text-gray-soft hover:text-sage hover:bg-cream rounded-lg transition-all duration-200"
                  title="Send Feedback"
                  data-testid="nav-feedback-btn"
                >
                  <MessageSquarePlus size={18} />
                </button>
                {user?.is_admin && (
                  <button
                    onClick={() => navigate('/admin')}
                    className={`p-1.5 rounded-lg transition-all duration-200 ${
                      location.pathname.startsWith('/admin')
                        ? 'text-charcoal bg-cream'
                        : 'text-gray-soft hover:text-charcoal hover:bg-cream'
                    }`}
                    title="Admin Console"
                    data-testid="admin-nav-btn"
                  >
                    <Shield size={18} />
                  </button>
                )}
                {/* Clickable Profile Avatar - opens Settings Modal */}
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                  title="Profile Settings"
                  data-testid="profile-avatar-btn"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-rose-light flex items-center justify-center shrink-0">
                      <User size={16} className="text-rose-dark" />
                    </div>
                  )}
                  <span className="text-sm text-charcoal hidden xl:inline whitespace-nowrap">{user?.name}</span>
                </button>
              </div>
            </div>

            {/* Mobile Header - Right Side Icons */}
            <div className="lg:hidden flex items-center space-x-2">
              {/* Admin icon */}
              {user?.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className={`p-2 rounded-lg transition-colors ${
                    location.pathname.startsWith('/admin')
                      ? 'text-charcoal bg-cream'
                      : 'text-gray-soft hover:text-charcoal'
                  }`}
                  title="Admin Console"
                  data-testid="mobile-admin-icon-btn"
                >
                  <Shield size={20} />
                </button>
              )}
              {/* Profile Avatar - opens Settings Modal */}
              <button
                onClick={() => setShowSettings(true)}
                className="hover:opacity-80 transition-opacity"
                title="Profile Settings"
                data-testid="mobile-profile-avatar-btn"
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-rose-light flex items-center justify-center">
                    <User size={16} className="text-rose-dark" />
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Settings Modal - rendered outside nav for proper z-index */}
      <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
      
      {/* Feedback Modal - rendered outside nav for proper z-index */}
      <FeedbackWidget isOpen={showFeedback} onClose={() => setShowFeedback(false)} />

      {/* Mobile Bottom Tab Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-beige z-50 safe-area-bottom">
        <div className="flex items-center py-2">
          {/* Main 3 tabs - take up most of the space */}
          <div className="flex-1 flex justify-around items-center">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`flex flex-col items-center gap-1 px-4 py-2 min-w-[70px] transition-colors ${
                    active ? 'text-sage' : 'text-gray-soft'
                  }`}
                  data-testid={`mobile-tab-${item.path.slice(1)}`}
                >
                  <Icon size={22} strokeWidth={active ? 2.5 : 2} />
                  <span className={`text-xs ${active ? 'font-medium' : ''}`}>{item.label}</span>
                </button>
              );
            })}
          </div>
          
          {/* Feedback icon on the right */}
          <button
            onClick={() => setShowFeedback(true)}
            className="flex flex-col items-center gap-1 px-3 py-2 text-gray-soft hover:text-sage transition-colors"
            data-testid="mobile-feedback-btn"
            aria-label="Send Feedback"
          >
            <MessageSquarePlus size={22} />
            <span className="text-xs">Feedback</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Navigation;
